import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Archive, Loader2, Save, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { errorCode } from "@/lib/api-errors";
import type {
  CreateInstanceEmployeeRequest,
  EmployeeTemplate,
  InstanceEmployee,
  UpdateInstanceEmployeeRequest,
} from "@/types/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InstanceEmployeeConfigBuilder } from "./instance-employee-config-builder";
import { VoiceTestCallModal } from "@/components/voice-test-call-modal";
import { LanguagesField } from "@/components/languages-field";
import { PromptEditor } from "@/components/prompt-editor";
import { FieldExposure } from "@/components/field-exposure";
import {
  EXPOSABLE_FIELD_KEYS,
  type InstanceEmployeeConfig,
  type InstanceIntegration,
} from "@/types/employee-config";

const EMPLOYEE_TYPES = [
  "product_sales",
  "service_sales",
  "support",
  "cold_outreach",
  "voice_outreach",
  "dm_outreach",
  "lead_qualifier",
  "sales_consultant",
  "account_manager",
  "quality_tracker",
  "booking",
  "email_responder",
];

/**
 * Create/edit page for a centrally-managed instance employee:
 *   /instances/:name/employees/new
 *   /instances/:name/employees/:employeeId
 *
 * Create mode: pick a system template (optional), name the bot.
 * Edit mode: name/type, config JSON, custom prompt (markdown editor,
 * same pattern as the template prompt editor), archive.
 */
export function InstanceEmployeeFormPage() {
  const { t } = useTranslation("instances");
  const { name = "", employeeId } = useParams<{
    name: string;
    employeeId?: string;
  }>();
  const isCreate = !employeeId || employeeId === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const employeeQuery = useQuery({
    queryKey: ["instance", name, "employees", employeeId],
    queryFn: () =>
      api.get<{ employee: InstanceEmployee }>(
        `/api/admin/instances/${name}/employees/${employeeId}`
      ),
    enabled: !isCreate,
  });

  const templatesQuery = useQuery({
    queryKey: ["employee-templates"],
    queryFn: () =>
      api.get<{ templates: EmployeeTemplate[] }>(
        "/api/admin/employee-templates"
      ),
  });

  // Real integrations on the instance — used to decide whether the employee
  // has a voice channel bound and can therefore place a "call myself" test
  // call. Shares its query key with the config builder, so no extra fetch.
  const integrationsQuery = useQuery({
    queryKey: ["instance", name, "integrations"],
    queryFn: () =>
      api.get<{ integrations: InstanceIntegration[]; unreachable?: boolean }>(
        `/api/admin/instances/${name}/integrations`
      ),
    enabled: !!name,
  });

  const [botName, setBotName] = React.useState("");
  const [employeeType, setEmployeeType] = React.useState("lead_qualifier");
  const [showVoiceTest, setShowVoiceTest] = React.useState(false);
  const [sourceTemplateId, setSourceTemplateId] = React.useState<string>("");
  const [config, setConfig] = React.useState<InstanceEmployeeConfig>({});
  const [prompt, setPrompt] = React.useState("");
  const [initialized, setInitialized] = React.useState(false);

  // Voice-only templates always get the "Test call" button; any other
  // employee qualifies as soon as a voice integration is bound in its
  // config. Chat and email bots are tested in their own channel — voice is
  // the one channel that needs a real outbound call.
  const voiceIntegrationIds = new Set(
    (integrationsQuery.data?.integrations ?? [])
      .filter((i) => i.type === "voice")
      .map((i) => i.id)
  );
  const canVoiceTest =
    employeeType === "voice_outreach" ||
    (config.channelIntegrationIds ?? []).some((id) =>
      voiceIntegrationIds.has(id)
    );

  React.useEffect(() => {
    const e = employeeQuery.data?.employee;
    if (e && !initialized) {
      setBotName(e.name);
      setEmployeeType(e.employeeType);
      setConfig((e.config ?? {}) as InstanceEmployeeConfig);
      setPrompt(e.promptGenerator ?? "");
      setInitialized(true);
    }
  }, [employeeQuery.data, initialized]);

  // A template-less bot compiles its prompt from the active template of its
  // type; the API refuses a type that has none (NO_TEMPLATE_FOR_TYPE).
  const saveErrorMessage = (err: unknown, fallback: string): string =>
    errorCode(err) === "NO_TEMPLATE_FOR_TYPE"
      ? t("employeeForm.noTemplateForType", { type: employeeType })
      : err instanceof Error
        ? err.message
        : fallback;

  const createMutation = useMutation({
    mutationFn: (payload: CreateInstanceEmployeeRequest) =>
      api.post<{ employee: InstanceEmployee }>(
        `/api/admin/instances/${name}/employees`,
        payload
      ),
    onSuccess: (res) => {
      toast.success(t("employeeForm.created"));
      qc.invalidateQueries({ queryKey: ["instance", name, "employees"] });
      navigate(`/instances/${name}/employees/${res.employee.id}`, {
        replace: true,
      });
    },
    onError: (err: unknown) => {
      toast.error(saveErrorMessage(err, t("employeeForm.createFailed")));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateInstanceEmployeeRequest) =>
      api.put<{ employee: InstanceEmployee }>(
        `/api/admin/instances/${name}/employees/${employeeId}`,
        payload
      ),
    onSuccess: (res) => {
      toast.success(t("employeeForm.saved", { version: res.employee.version }));
      qc.setQueryData(["instance", name, "employees", employeeId], res);
      qc.invalidateQueries({ queryKey: ["instance", name, "employees"] });
    },
    onError: (err: unknown) => {
      toast.error(saveErrorMessage(err, t("employeeForm.saveFailed")));
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () =>
      api.del<{ employee: InstanceEmployee }>(
        `/api/admin/instances/${name}/employees/${employeeId}`
      ),
    onSuccess: () => {
      toast.success(t("employeeForm.archived"));
      qc.invalidateQueries({ queryKey: ["instance", name, "employees"] });
      navigate(`/instances/${name}`);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("employeeForm.archiveFailed"));
    },
  });

  function handleCreate() {
    if (!botName.trim()) {
      toast.error(t("employeeForm.nameRequired"));
      return;
    }
    createMutation.mutate({
      name: botName.trim(),
      ...(sourceTemplateId ? { sourceTemplateId } : { employeeType }),
    });
  }

  function setLanguages(langs: string[]) {
    const idf = { ...(config.initialDefaults ?? {}) };
    if (langs.length) idf.languages = langs;
    else delete idf.languages;
    const next: InstanceEmployeeConfig = { ...config };
    if (Object.keys(idf).length) next.initialDefaults = idf;
    else delete next.initialDefaults;
    setConfig(next);
  }

  // Text defaults seeded onto the tenant employee at FIRST delivery only
  // (initialDefaults are seed-once — the tenant owns the values after).
  function setInitialDefault(
    key: "greeting" | "emailGreeting" | "emailSignature",
    value: string
  ): void;
  function setInitialDefault(
    key: "voiceGreetingDelaySeconds",
    value: number | undefined
  ): void;
  function setInitialDefault(
    key:
      | "greeting"
      | "emailGreeting"
      | "emailSignature"
      | "voiceGreetingDelaySeconds",
    value: string | number | undefined
  ) {
    const idf = { ...(config.initialDefaults ?? {}) };
    if (key === "voiceGreetingDelaySeconds") {
      // Stored as a number or absent — never a string.
      if (typeof value === "number" && Number.isFinite(value)) idf[key] = value;
      else delete idf[key];
    } else if (typeof value === "string" && value) idf[key] = value;
    else delete idf[key];
    const next: InstanceEmployeeConfig = { ...config };
    if (Object.keys(idf).length) next.initialDefaults = idf;
    else delete next.initialDefaults;
    setConfig(next);
  }

  // Per-field tenant exposure ("Show in user UI") for form-level fields.
  const exposedFields = Array.isArray(config.tenantEditableFields)
    ? (config.tenantEditableFields as string[])
    : [];
  function toggleExposed(fieldKey: string, on: boolean) {
    const set = new Set(exposedFields);
    if (on) set.add(fieldKey);
    else set.delete(fieldKey);
    const next: InstanceEmployeeConfig = { ...config };
    if (set.size) next.tenantEditableFields = [...set];
    else delete next.tenantEditableFields;
    setConfig(next);
  }

  function handleSave() {
    if (!botName.trim()) {
      toast.error(t("employeeForm.nameRequired"));
      return;
    }
    updateMutation.mutate({
      name: botName.trim(),
      employeeType,
      config,
      promptGenerator: prompt.trim() === "" ? null : prompt,
    });
  }

  const employee = employeeQuery.data?.employee;
  const archived = employee?.syncStatus === "archived";

  if (!isCreate && employeeQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to={`/instances/${name}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> {name}
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {isCreate ? t("employeeForm.newTitle") : botName || t("employeeForm.fallbackTitle")}
          </h1>
          {employee && <Badge variant="secondary">{employee.syncStatus}</Badge>}
          {employee?.syncStatus === "failed" && employee.syncError && (
            <span className="text-sm text-destructive">{employee.syncError}</span>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isCreate ? t("employeeForm.createTitle") : t("employeeForm.settingsTitle")}</CardTitle>
          <CardDescription>
            {isCreate
              ? t("employeeForm.createDescription")
              : employee?.syncStatus === "draft"
                ? t("employeeForm.draftDescription")
                : t("employeeForm.editDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ie-name">{t("employeeForm.name")}</Label>
              <Input
                id="ie-name"
                value={botName}
                onChange={(e) => setBotName(e.target.value)}
                placeholder={t("employeeForm.namePlaceholder")}
                disabled={archived}
              />
            </div>

            {isCreate ? (
              <div className="space-y-1.5">
                <Label htmlFor="ie-template">{t("employeeForm.fromTemplate")}</Label>
                <Select
                  value={sourceTemplateId || "__scratch__"}
                  onValueChange={(v) =>
                    setSourceTemplateId(v === "__scratch__" ? "" : v)
                  }
                >
                  <SelectTrigger id="ie-template">
                    <SelectValue placeholder={t("employeeForm.fromScratch")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__scratch__">{t("employeeForm.fromScratch")}</SelectItem>
                    {(templatesQuery.data?.templates ?? []).map((tpl) => (
                      <SelectItem key={tpl.templateId} value={tpl.templateId}>
                        {tpl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {(isCreate && !sourceTemplateId) || !isCreate ? (
              <div className="space-y-1.5">
                <Label htmlFor="ie-type">{t("employeeForm.employeeType")}</Label>
                <Select
                  value={employeeType}
                  onValueChange={setEmployeeType}
                  disabled={archived}
                >
                  <SelectTrigger id="ie-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYEE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>

          {!isCreate && initialized && employeeType !== "email_responder" && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>{t("employeeForm.languages")}</Label>
                <FieldExposure
                  exposed={exposedFields.includes(
                    EXPOSABLE_FIELD_KEYS.languages
                  )}
                  onToggle={(on) =>
                    toggleExposed(EXPOSABLE_FIELD_KEYS.languages, on)
                  }
                  disabled={archived}
                  label={t("employeeForm.languages")}
                />
              </div>
              <LanguagesField
                value={config.initialDefaults?.languages ?? []}
                onChange={setLanguages}
                disabled={archived}
              />
            </div>
          )}

          {/* Tenant-editable defaults + per-field visibility. The text
              defaults are seed-once (applied on first delivery; the tenant
              owns the values after) — the "Show in user UI" toggles decide
              what the tenant may edit and sync on EVERY save. */}
          {!isCreate && initialized && (
            <>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="ie-greeting">{t("employeeForm.greeting")}</Label>
                  <FieldExposure
                    exposed={exposedFields.includes(
                      EXPOSABLE_FIELD_KEYS.greeting
                    )}
                    onToggle={(on) =>
                      toggleExposed(EXPOSABLE_FIELD_KEYS.greeting, on)
                    }
                    disabled={archived}
                    label={t("employeeForm.greeting")}
                  />
                </div>
                <Input
                  id="ie-greeting"
                  value={config.initialDefaults?.greeting ?? ""}
                  onChange={(e) => setInitialDefault("greeting", e.target.value)}
                  placeholder="Hello! I'm {{employee_name}} — how can I help?"
                  disabled={archived}
                />
                <p className="text-xs text-muted-foreground">
                  {t("employeeForm.seededHint")}
                </p>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="ie-voice-greeting-delay">
                    {t("employeeForm.greetingDelaySeconds")}
                  </Label>
                  <FieldExposure
                    exposed={exposedFields.includes(
                      EXPOSABLE_FIELD_KEYS.voiceGreetingDelay
                    )}
                    onToggle={(on) =>
                      toggleExposed(EXPOSABLE_FIELD_KEYS.voiceGreetingDelay, on)
                    }
                    disabled={archived}
                    label={t("employeeForm.greetingDelay")}
                  />
                </div>
                <Input
                  id="ie-voice-greeting-delay"
                  type="number"
                  min={0}
                  max={10}
                  step={0.5}
                  value={config.initialDefaults?.voiceGreetingDelaySeconds ?? ""}
                  onChange={(e) =>
                    setInitialDefault(
                      "voiceGreetingDelaySeconds",
                      e.target.value === "" ? undefined : Number(e.target.value)
                    )
                  }
                  disabled={archived}
                />
                <p className="text-xs text-muted-foreground">
                  {t("employeeForm.greetingDelayHint")}
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="ie-email-greeting">{t("employeeForm.emailGreeting")}</Label>
                    <FieldExposure
                      exposed={exposedFields.includes(
                        EXPOSABLE_FIELD_KEYS.emailGreeting
                      )}
                      onToggle={(on) =>
                        toggleExposed(EXPOSABLE_FIELD_KEYS.emailGreeting, on)
                      }
                      disabled={archived}
                      label={t("employeeForm.emailGreeting")}
                    />
                  </div>
                  <Input
                    id="ie-email-greeting"
                    value={config.initialDefaults?.emailGreeting ?? ""}
                    onChange={(e) =>
                      setInitialDefault("emailGreeting", e.target.value)
                    }
                    placeholder="Dear {{contact_name}},"
                    disabled={archived}
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="ie-email-signature">{t("employeeForm.emailSignature")}</Label>
                    <FieldExposure
                      exposed={exposedFields.includes(
                        EXPOSABLE_FIELD_KEYS.emailSignature
                      )}
                      onToggle={(on) =>
                        toggleExposed(EXPOSABLE_FIELD_KEYS.emailSignature, on)
                      }
                      disabled={archived}
                      label={t("employeeForm.emailSignature")}
                    />
                  </div>
                  <Textarea
                    id="ie-email-signature"
                    value={config.initialDefaults?.emailSignature ?? ""}
                    onChange={(e) =>
                      setInitialDefault("emailSignature", e.target.value)
                    }
                    placeholder={"Best regards,\n{{employee_name}}"}
                    rows={3}
                    disabled={archived}
                    className="font-mono text-xs"
                  />
                </div>
              </div>

              {/* Tenant-owned sections: there is no operator value to set —
                  exposing them only unlocks the section in the tenant
                  editor (working hours, KPIs / metrics). */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                <div className="flex items-center gap-3">
                  <Label>{t("employeeForm.workingHours")}</Label>
                  <FieldExposure
                    exposed={exposedFields.includes(
                      EXPOSABLE_FIELD_KEYS.workingHours
                    )}
                    onToggle={(on) =>
                      toggleExposed(EXPOSABLE_FIELD_KEYS.workingHours, on)
                    }
                    disabled={archived}
                    label={t("employeeForm.workingHours")}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Label>{t("employeeForm.kpis")}</Label>
                  <FieldExposure
                    exposed={exposedFields.includes(EXPOSABLE_FIELD_KEYS.kpis)}
                    onToggle={(on) =>
                      toggleExposed(EXPOSABLE_FIELD_KEYS.kpis, on)
                    }
                    disabled={archived}
                    label={t("employeeForm.kpis")}
                  />
                </div>
              </div>
            </>
          )}

          {isCreate && (
            <div className="flex justify-end">
              <Button onClick={handleCreate} disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("employeeForm.create")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {!isCreate && initialized && (
        <Tabs defaultValue="behaviour">
          <TabsList className="flex h-auto flex-wrap">
            <TabsTrigger value="behaviour">{t("employeeForm.tabs.behaviour")}</TabsTrigger>
            <TabsTrigger value="voice">{t("employeeForm.tabs.voice")}</TabsTrigger>
            <TabsTrigger value="runtime">{t("employeeForm.tabs.runtime")}</TabsTrigger>
            <TabsTrigger value="channels">{t("employeeForm.tabs.channels")}</TabsTrigger>
          </TabsList>

          <TabsContent value="behaviour" className="space-y-6">
            {/* Type-specific: builder renders its email OR behaviour card. */}
            <InstanceEmployeeConfigBuilder
              config={config}
              onChange={setConfig}
              disabled={archived}
              employeeType={employeeType}
              instanceName={name}
              section="email"
            />
            <InstanceEmployeeConfigBuilder
              config={config}
              onChange={setConfig}
              disabled={archived}
              employeeType={employeeType}
              instanceName={name}
              section="behaviour"
            />
            <Card>
              <CardHeader>
                <CardTitle>{t("employeeForm.promptTitle")}</CardTitle>
                <CardDescription>
                  {employee?.sourceTemplateId
                    ? t("employeeForm.promptDescriptionWithTemplate", {
                        template: employee.sourceTemplateId,
                      })
                    : t("employeeForm.promptDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div className="space-y-0.5 pr-4">
                    <Label htmlFor="use-manual-prompt">
                      {t("employeeForm.manualPromptToggle")}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t("employeeForm.manualPromptToggleHint")}
                    </p>
                  </div>
                  <Switch
                    id="use-manual-prompt"
                    checked={config.useManualPrompt === true}
                    disabled={archived}
                    onCheckedChange={(checked) =>
                      setConfig((c) => ({ ...c, useManualPrompt: checked }))
                    }
                  />
                </div>
                {config.useManualPrompt ? (
                  <div className="space-y-2">
                    <Label htmlFor="manual-system-prompt">
                      {t("employeeForm.manualPromptLabel")}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t("employeeForm.manualPromptHint")}
                    </p>
                    <Textarea
                      id="manual-system-prompt"
                      className="min-h-[320px] font-mono text-xs"
                      disabled={archived}
                      value={config.manualSystemPrompt ?? ""}
                      onChange={(e) =>
                        setConfig((c) => ({
                          ...c,
                          manualSystemPrompt: e.target.value,
                        }))
                      }
                    />
                  </div>
                ) : (
                  <PromptEditor value={prompt} onChange={setPrompt} />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="voice">
            <InstanceEmployeeConfigBuilder
              config={config}
              onChange={setConfig}
              disabled={archived}
              employeeType={employeeType}
              instanceName={name}
              section="voice"
            />
          </TabsContent>

          <TabsContent value="runtime">
            <InstanceEmployeeConfigBuilder
              config={config}
              onChange={setConfig}
              disabled={archived}
              employeeType={employeeType}
              instanceName={name}
              section="runtime"
            />
          </TabsContent>

          <TabsContent value="channels">
            <InstanceEmployeeConfigBuilder
              config={config}
              onChange={setConfig}
              disabled={archived}
              employeeType={employeeType}
              instanceName={name}
              section="channels"
            />
          </TabsContent>
        </Tabs>
      )}

      {!isCreate && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              variant="destructive"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending || archived}
            >
              <Archive className="mr-2 h-4 w-4" />
              {t("employeeForm.archive")}
            </Button>
            <div className="flex items-center gap-2">
              {canVoiceTest && employeeId && (
                <Button
                  variant="outline"
                  onClick={() => setShowVoiceTest(true)}
                  disabled={archived}
                >
                  <Phone className="mr-2 h-4 w-4" />
                  {t("employeeForm.testCall")}
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending || archived}
              >
                {updateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {t("employeeForm.save")}
              </Button>
            </div>
          </div>

          {canVoiceTest && employeeId && (
            <VoiceTestCallModal
              instanceName={name}
              employeeId={employeeId}
              open={showVoiceTest}
              onClose={() => setShowVoiceTest(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
